import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database, Loader2, ExternalLink } from "lucide-react";
import { toast } from "@/lib/safeToast";
import { FunctionsHttpError } from "@supabase/supabase-js";

interface ImportResult {
  imported: number;
  inserted: number;
  updated: number;
  failed: number;
  total: number;
  errors?: { name: string; error: string }[];
}

export function PsychonautWikiImportPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("pw-import", { body: {} });
      if (error) {
        const details = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
        console.error("pw-import failed:", details);
        toast.error("Falha na importação PsychonautWiki", { description: details.slice(0, 200) });
        return;
      }
      const r = data as ImportResult;
      setResult(r);
      toast.success("PsychonautWiki importada", {
        description: `${r.inserted} novas • ${r.updated} atualizadas • ${r.failed} falhas`,
      });
    } catch (err: any) {
      toast.error("Erro inesperado", { description: String(err?.message ?? err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
            <Database className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">Base PsychonautWiki</h3>
              <Badge variant="secondary" className="text-[10px]">CC-BY-SA</Badge>
              <Badge variant="outline" className="text-[10px]">API pública</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Importa dose, duração (onset/come-up/peak/offset/total/afterglow por ROA),
              tolerância, efeitos, classes químicas e potencial de dependência da PsychonautWiki
              para a sua base global de substâncias. Idempotente — pode rodar novamente.
            </p>
            <a
              href="https://psychonautwiki.org"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2"
            >
              psychonautwiki.org <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <Button onClick={run} disabled={loading} className="shrink-0">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
          {loading ? "Importando…" : "Importar / atualizar"}
        </Button>
      </div>

      {result && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t">
          <Stat label="Total recebido" value={result.total} />
          <Stat label="Novas" value={result.inserted} tone="ok" />
          <Stat label="Atualizadas" value={result.updated} tone="info" />
          <Stat label="Falhas" value={result.failed} tone={result.failed ? "warn" : undefined} />
          {result.errors && result.errors.length > 0 && (
            <div className="col-span-full text-xs text-muted-foreground">
              <div className="font-medium mb-1">Primeiras falhas:</div>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <li key={i}><span className="font-mono">{e.name}</span>: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" | "info" }) {
  const color =
    tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : tone === "info" ? "text-primary"
    : "text-foreground";
  return (
    <div className="rounded-md border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
